import React, { useState } from 'react';
import { BookOpen, Download, Globe } from 'lucide-react';

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SUPPORT = 'pedidos@keypubli.com';
const TOPICS = 'casino, cbd, gambling, cryptocurrency, sex, erotic, vpn, tarot, cannabis';

const T = {
  es: {
    version: 'API de KeyPubli · Versión 1.0',
    title: 'Documentación de la API del Catálogo',
    subtitle: 'Accede al catálogo completo de medios de KeyPubli (precios, métricas y temáticas) para integrarlo en tu marketplace, comparador o herramienta.',
    intro: 'Introducción',
    introItems: ['La API es de solo lectura y solo devuelve medios activos y disponibles para la venta.', 'Todas las respuestas son JSON (UTF-8), excepto el endpoint CSV.', 'Los precios están en euros (EUR).', 'Los datos se devuelven en tiempo real en cada llamada.'],
    base: 'URL base',
    auth: 'Autenticación',
    authText: 'Cada petición necesita un token personal proporcionado por KeyPubli. Envíalo preferiblemente en la cabecera Authorization:',
    authAlt: 'Como alternativa se acepta el parámetro _token (body JSON, form-data o query string). Mantén tu token en secreto: nunca lo expongas en código del navegador ni en repositorios públicos.',
    retrieveTitle: 'POST /products/retrieve — Catálogo JSON',
    retrieveText: 'Devuelve los medios en JSON. Sin el parámetro page devuelve el catálogo completo (hasta 50.000). Con page se pagina en bloques de 1.000.',
    params: 'Parámetros', name: 'Nombre', type: 'Tipo', required: 'Obligatorio', desc: 'Descripción', no: 'No',
    pTok: 'Token de acceso (si no usas la cabecera Authorization).', pPage: 'Página (1, 2, 3…). 1.000 productos por página. Opcional.',
    example: 'Ejemplo de respuesta',
    pagination: 'Campos de paginación: total (todos los productos), count (en esta respuesta), page, page_size, total_pages (solo si se envía page).',
    changesTitle: 'POST /products/changes — Solo cambios',
    changesText: 'Devuelve solo lo ocurrido desde la fecha since (obligatoria, ISO 8601): added (medios nuevos), updated (medios modificados) y removed (id, website y removed_at de los medios retirados). Úsalo para sincronizar cada hora sin descargar todo el catálogo.',
    pSince: 'Fecha desde la que quieres los cambios, p. ej. 2026-10-01T00:00:00Z. Obligatorio.',
    csvTitle: 'GET /products/csv — Catálogo CSV',
    csvText: 'Descarga el catálogo completo en CSV: separador punto y coma (;), UTF-8 con BOM (se abre directamente en Excel). Las columnas coinciden con los campos del producto; los booleanos son "Sí"/"No" y los precios no disponibles van vacíos.',
    fields: 'Campos del producto', field: 'Campo',
    f: {
      id: 'ID único y estable del producto. Úsalo para sincronizar.', website: 'Dominio del medio (sin http/www).', product_url: 'URL pública del medio en el catálogo de KeyPubli.',
      country: 'País del medio.', category: 'Categorías (separadas por comas).', language: 'Idioma(s) del medio.', dr: 'Domain Rating (Ahrefs).', da: 'Domain Authority (Moz).',
      traffic: 'Tráfico orgánico mensual estimado.', site_type: 'Tipo de medio (Web, Blog, Periódico…).', link_type: 'Tipo de enlace (Dofollow / Nofollow).', link_duration: 'Duración del enlace (Permanente, 1 año…).',
      sponsored_tag: '"Sí" si el artículo se etiqueta como patrocinado.', pvp: 'Precio del artículo patrocinado (EUR).', pvp_sensible: 'Precio para temáticas sensibles (EUR). null si no se acepta.',
      pvp_insercion: 'Precio de inserción de enlace en un artículo existente. null si no se ofrece.', pvp_insercion_sensible: 'Precio de inserción para temáticas sensibles.',
      pvp_mencion: 'Precio de mención de marca. null si no se ofrece.', pvp_mencion_sensible: 'Precio de mención para temáticas sensibles.',
      accepts_insertion: 'Acepta inserciones de enlace.', accepts_mention: 'Acepta menciones de marca.', accepts_sensitive: 'Acepta al menos una temática sensible.', topics: 'Temáticas sensibles aceptadas, un campo por temática.',
      status: '"new" si se añadió en los últimos 30 días, "updated" si cambió en los últimos 30 días, si no "unchanged".', created_at: 'Fecha en que el medio se añadió al catálogo (ISO 8601).', updated_at: 'Fecha del último cambio visible (precio, métricas, temáticas…).',
    },
    code: 'Ejemplos de código',
    limits: 'Límites de uso',
    limitItems: ['120 peticiones por minuto por token y 240 por minuto por IP.', '10 intentos con token inválido en 15 minutos bloquean la IP durante 15 minutos.', 'Cada respuesta incluye las cabeceras X-RateLimit-Limit y X-RateLimit-Remaining.', 'Recomendamos sincronizar de 1 a 4 veces al día (como máximo cada 20 minutos).'],
    codes: 'Códigos de respuesta', code_: 'Código', meaning: 'Significado',
    c: { 200: 'Petición correcta.', 400: 'Falta el token.', 401: 'Token inválido o desactivado.', 429: 'Límite superado o IP bloqueada temporalmente. Respeta la cabecera Retry-After (segundos).', '5xx': 'Error temporal. Reintenta con espera exponencial.' },
    best: 'Buenas prácticas',
    bestItems: ['Carga todo una vez con /products/retrieve y después llama cada hora a /products/changes con la fecha de tu última sincronización.', 'Usa el campo id como clave de sincronización: el dominio puede cambiar, el id no.', 'Si un id desaparece, el medio ya no está disponible: elimínalo de tu listado.', 'Ante un 429, espera los segundos indicados en Retry-After antes de reintentar.', 'Enlaza a product_url para que tus usuarios vean el medio en KeyPubli.'],
    contact: '¿Necesitas un token o tienes dudas? Escríbenos a',
    pdf: 'Descargar PDF',
  },
  en: {
    version: 'KeyPubli API · Version 1.0',
    title: 'Catalog API Documentation',
    subtitle: "Access KeyPubli's full media catalog (prices, metrics and topics) to integrate it into your marketplace, comparator or tool.",
    intro: 'Introduction',
    introItems: ['The API is read-only and only returns media that are active and available for sale.', 'All responses are JSON (UTF-8), except the CSV endpoint.', 'Prices are in euros (EUR).', 'Data is returned in real time on every call.'],
    base: 'Base URL',
    auth: 'Authentication',
    authText: 'Every request needs a personal token provided by KeyPubli. Preferably send it in the Authorization header:',
    authAlt: 'Alternatively the _token parameter is accepted (JSON body, form-data or query string). Keep your token secret: never expose it in browser code or public repositories.',
    retrieveTitle: 'POST /products/retrieve — JSON catalog',
    retrieveText: 'Returns the media as JSON. Without the page parameter it returns the full catalog (up to 50,000). With page it is paginated in blocks of 1,000.',
    params: 'Parameters', name: 'Name', type: 'Type', required: 'Required', desc: 'Description', no: 'No',
    pTok: 'Access token (if you do not use the Authorization header).', pPage: 'Page (1, 2, 3…). 1,000 products per page. Optional.',
    example: 'Example response',
    pagination: 'Pagination fields: total (all products), count (in this response), page, page_size, total_pages (only when page is sent).',
    changesTitle: 'POST /products/changes — Changes only',
    changesText: 'Returns only what happened since the given since date (required, ISO 8601): added (new media), updated (modified media) and removed (id, website and removed_at of withdrawn media). Use it to sync hourly without downloading the whole catalog.',
    pSince: 'Date you want changes from, e.g. 2026-10-01T00:00:00Z. Required.',
    csvTitle: 'GET /products/csv — CSV catalog',
    csvText: 'Downloads the full catalog as CSV: semicolon (;) delimiter, UTF-8 with BOM (opens directly in Excel). Columns match the product fields; booleans are "Sí"/"No" (yes/no) and unavailable prices are empty.',
    fields: 'Product fields', field: 'Field',
    f: {
      id: 'Unique, stable product ID. Use it to sync.', website: 'Media domain (no http/www).', product_url: 'Public media URL in the KeyPubli catalog.',
      country: 'Media country.', category: 'Categories (comma separated).', language: 'Media language(s).', dr: 'Domain Rating (Ahrefs).', da: 'Domain Authority (Moz).',
      traffic: 'Estimated monthly organic traffic.', site_type: 'Media type (Web, Blog, Newspaper…).', link_type: 'Link type (Dofollow / Nofollow).', link_duration: 'Link duration (Permanent, 1 year…).',
      sponsored_tag: '"Sí" (yes) if the article is labelled as sponsored.', pvp: 'Sponsored article price (EUR).', pvp_sensible: 'Price for sensitive topics (EUR). null if not accepted.',
      pvp_insercion: 'Link insertion price in an existing article. null if not offered.', pvp_insercion_sensible: 'Insertion price for sensitive topics.',
      pvp_mencion: 'Brand mention price. null if not offered.', pvp_mencion_sensible: 'Mention price for sensitive topics.',
      accepts_insertion: 'Accepts link insertions.', accepts_mention: 'Accepts brand mentions.', accepts_sensitive: 'Accepts at least one sensitive topic.', topics: 'Accepted sensitive topics, one per field.',
      status: '"new" if added in the last 30 days, "updated" if changed in the last 30 days, otherwise "unchanged".', created_at: 'Date the media was added to the catalog (ISO 8601).', updated_at: 'Date of the last visible change (price, metrics, topics…).',
    },
    code: 'Code examples',
    limits: 'Rate limits',
    limitItems: ['120 requests per minute per token and 240 per minute per IP.', '10 invalid-token attempts within 15 minutes block the IP for 15 minutes.', 'Every response includes the X-RateLimit-Limit and X-RateLimit-Remaining headers.', 'We recommend syncing 1–4 times a day (at most every 20 minutes).'],
    codes: 'Response codes', code_: 'Code', meaning: 'Meaning',
    c: { 200: 'Successful request.', 400: 'Token is missing.', 401: 'Invalid or disabled token.', 429: 'Rate limit exceeded or IP temporarily blocked. Honour the Retry-After header (seconds).', '5xx': 'Temporary error. Retry with exponential backoff.' },
    best: 'Best practices',
    bestItems: ['Load everything once with /products/retrieve, then call /products/changes hourly with the date of your last sync.', 'Use the id field as your sync key: the domain may change, the id does not.', 'If an id disappears, the media is no longer available: remove it from your listing.', 'On a 429, wait the seconds given in Retry-After before retrying.', 'Link to product_url so your users can see the media on KeyPubli.'],
    contact: 'Need a token or have questions? Write to',
    pdf: 'Download PDF',
  },
};

const FIELD_TYPES = [
  ['id', 'string'], ['website', 'string'], ['product_url', 'string'], ['country', 'string'], ['category', 'string'], ['language', 'string'],
  ['dr', 'integer'], ['da', 'integer'], ['traffic', 'integer'], ['site_type', 'string'], ['link_type', 'string'], ['link_duration', 'string'],
  ['sponsored_tag', 'string'], ['pvp', 'number'], ['pvp_sensible', 'number | null'], ['pvp_insercion', 'number | null'], ['pvp_insercion_sensible', 'number | null'],
  ['pvp_mencion', 'number | null'], ['pvp_mencion_sensible', 'number | null'], ['accepts_insertion', 'boolean'], ['accepts_mention', 'boolean'], ['accepts_sensitive', 'boolean'],
  ['status', 'string'], ['created_at', 'string'], ['updated_at', 'string'],
];

const EXAMPLE = `{
  "success": true,
  "total": 2131,
  "count": 1000,
  "page": 1,
  "page_size": 1000,
  "total_pages": 3,
  "products": [
    {
      "id": "3f2a9c1e-…",
      "website": "example.com",
      "product_url": "https://app.keypubli.com/?search=example.com",
      "country": "España",
      "category": "TECNOLOGIA, NEGOCIOS",
      "language": "Español",
      "dr": 45, "da": 38, "traffic": 12500,
      "site_type": "Blog",
      "link_type": "Dofollow",
      "link_duration": "Permanente",
      "sponsored_tag": "No",
      "pvp": 120.0,
      "pvp_sensible": 180.0,
      "pvp_insercion": 90.0, "pvp_insercion_sensible": 135.0,
      "pvp_mencion": null, "pvp_mencion_sensible": null,
      "accepts_insertion": true, "accepts_mention": false,
      "accepts_sensitive": true,
      "casino": true, "cbd": false, "gambling": true, "cryptocurrency": true,
      "sex": false, "erotic": false, "vpn": true, "tarot": false, "cannabis": false,
      "status": "new",
      "created_at": "2026-10-14T09:00:00+00:00",
      "updated_at": "2026-10-14T09:00:00+00:00"
    }
  ]
}`;

const CODE = {
  cURL: `# Changes since a date
curl -X POST "${BASE}/products/changes" \\
  -H "Authorization: Bearer YOUR_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"since": "2026-10-01T00:00:00Z"}'

# JSON (page 1)
curl -X POST "${BASE}/products/retrieve" \\
  -H "Authorization: Bearer YOUR_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"page": 1}'

# CSV
curl -L "${BASE}/products/csv" \\
  -H "Authorization: Bearer YOUR_TOKEN" \\
  -o catalog.csv`,
  Python: `import time
import requests

BASE = "${BASE}"
HEADERS = {"Authorization": "Bearer YOUR_TOKEN"}

def fetch_all():
    products, page = [], 1
    while True:
        r = requests.post(f"{BASE}/products/retrieve", json={"page": page}, headers=HEADERS, timeout=60)
        if r.status_code == 429:
            time.sleep(int(r.headers.get("Retry-After", 60)))
            continue
        r.raise_for_status()
        data = r.json()
        products += data["products"]
        if page >= data["total_pages"]:
            return products
        page += 1

items = fetch_all()
print(len(items), "products")`,
  JavaScript: `// Node.js 18+ (server side — never expose your token in the browser)
const BASE = "${BASE}";
const TOKEN = process.env.KEYPUBLI_TOKEN;

async function fetchAll() {
  const products = [];
  for (let page = 1; ; page++) {
    const res = await fetch(\`\${BASE}/products/retrieve\`, {
      method: "POST",
      headers: { Authorization: \`Bearer \${TOKEN}\`, "Content-Type": "application/json" },
      body: JSON.stringify({ page }),
    });
    if (res.status === 429) {
      await new Promise((r) => setTimeout(r, (Number(res.headers.get("Retry-After")) || 60) * 1000));
      page--; continue;
    }
    if (!res.ok) throw new Error(\`HTTP \${res.status}\`);
    const data = await res.json();
    products.push(...data.products);
    if (page >= data.total_pages) return products;
  }
}

fetchAll().then((p) => console.log(p.length, "products"));`,
};

const Pre = ({ children }) => <pre className="bg-slate-900 text-emerald-300 text-xs rounded-lg p-4 overflow-x-auto print:whitespace-pre-wrap">{children}</pre>;
const H2 = ({ children }) => <h2 className="text-xl font-bold text-slate-900 mt-10 mb-3 print:break-after-avoid">{children}</h2>;
const Th = ({ children }) => <th className="text-left px-3 py-2 bg-slate-50 border-b font-medium text-slate-700">{children}</th>;
const Td = ({ children, mono }) => <td className={`px-3 py-2 border-b align-top ${mono ? 'font-mono text-xs' : ''}`}>{children}</td>;

export default function ApiDocsPage() {
  const [lang, setLang] = useState(navigator.language?.startsWith('es') ? 'es' : 'en');
  const [tab, setTab] = useState('cURL');
  const t = T[lang];

  return (
    <div className="min-h-screen bg-white" data-testid="api-docs-page">
      <header className="border-b bg-white sticky top-0 z-10 print:hidden">
        <div className="max-w-4xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 font-semibold"><BookOpen className="h-5 w-5 text-rose-600" /> KeyPubli API</div>
          <div className="flex items-center gap-2">
            <div className="flex border rounded-md overflow-hidden text-sm">
              {['es', 'en'].map((l) => (
                <button key={l} onClick={() => setLang(l)} className={`px-3 py-1.5 transition-colors ${lang === l ? 'bg-rose-600 text-white' : 'hover:bg-slate-50'}`} data-testid={`api-docs-lang-${l}`}>{l.toUpperCase()}</button>
              ))}
            </div>
            <button onClick={() => window.print()} className="inline-flex items-center gap-1.5 text-sm border rounded-md px-3 py-1.5 hover:bg-slate-50" data-testid="api-docs-pdf-btn"><Download className="h-4 w-4" /> {t.pdf}</button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-10 text-slate-700 text-sm leading-relaxed">
        <p className="text-xs font-medium text-rose-600 uppercase tracking-wide">{t.version}</p>
        <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mt-2" data-testid="api-docs-title">{t.title}</h1>
        <p className="mt-3 text-base">{t.subtitle}</p>

        <H2>{t.intro}</H2>
        <ul className="list-disc pl-5 space-y-1">{t.introItems.map((i) => <li key={i}>{i}</li>)}</ul>
        <p className="mt-4 font-medium flex items-center gap-1"><Globe className="h-4 w-4" /> {t.base}</p>
        <Pre>{BASE}</Pre>

        <H2>{t.auth}</H2>
        <p>{t.authText}</p>
        <Pre>Authorization: Bearer YOUR_TOKEN</Pre>
        <p className="mt-2">{t.authAlt}</p>

        <H2>{t.retrieveTitle}</H2>
        <p className="mb-2"><span className="text-xs font-bold text-white bg-blue-600 px-2 py-0.5 rounded mr-2">POST</span><code>{BASE}/products/retrieve</code></p>
        <p>{t.retrieveText}</p>
        <p className="font-medium mt-4 mb-2">{t.params}</p>
        <table className="w-full border text-sm"><thead><tr><Th>{t.name}</Th><Th>{t.type}</Th><Th>{t.required}</Th><Th>{t.desc}</Th></tr></thead>
          <tbody>
            <tr><Td mono>_token</Td><Td>string</Td><Td>{t.no}</Td><Td>{t.pTok}</Td></tr>
            <tr><Td mono>page</Td><Td>integer</Td><Td>{t.no}</Td><Td>{t.pPage}</Td></tr>
          </tbody>
        </table>
        <p className="font-medium mt-4 mb-2">{t.example}</p>
        <Pre>{EXAMPLE}</Pre>
        <p className="mt-2">{t.pagination}</p>

        <H2>{t.changesTitle}</H2>
        <p className="mb-2"><span className="text-xs font-bold text-white bg-blue-600 px-2 py-0.5 rounded mr-2">POST</span><code>{BASE}/products/changes</code></p>
        <p>{t.changesText}</p>
        <table className="w-full border text-sm mt-3"><thead><tr><Th>{t.name}</Th><Th>{t.type}</Th><Th>{t.required}</Th><Th>{t.desc}</Th></tr></thead>
          <tbody><tr><Td mono>since</Td><Td>string</Td><Td>✔</Td><Td>{t.pSince}</Td></tr></tbody>
        </table>

        <H2>{t.csvTitle}</H2>
        <p className="mb-2"><span className="text-xs font-bold text-white bg-emerald-600 px-2 py-0.5 rounded mr-2">GET</span><code>{BASE}/products/csv</code></p>
        <p>{t.csvText}</p>

        <H2>{t.fields}</H2>
        <table className="w-full border text-sm" data-testid="api-docs-fields-table"><thead><tr><Th>{t.field}</Th><Th>{t.type}</Th><Th>{t.desc}</Th></tr></thead>
          <tbody>
            {FIELD_TYPES.map(([f, ty]) => <tr key={f}><Td mono>{f}</Td><Td>{ty}</Td><Td>{t.f[f]}</Td></tr>)}
            <tr><Td mono>{TOPICS}</Td><Td>boolean</Td><Td>{t.f.topics}</Td></tr>
          </tbody>
        </table>

        <H2>{t.code}</H2>
        <div className="flex gap-1 mb-2 print:hidden">
          {Object.keys(CODE).map((k) => (
            <button key={k} onClick={() => setTab(k)} className={`text-xs px-3 py-1.5 rounded-md border transition-colors ${tab === k ? 'bg-slate-900 text-white' : 'hover:bg-slate-50'}`} data-testid={`api-docs-code-${k}`}>{k}</button>
          ))}
        </div>
        <div className="print:hidden"><Pre>{CODE[tab]}</Pre></div>
        <div className="hidden print:block space-y-3">{Object.entries(CODE).map(([k, v]) => <div key={k}><p className="font-medium">{k}</p><Pre>{v}</Pre></div>)}</div>

        <H2>{t.limits}</H2>
        <ul className="list-disc pl-5 space-y-1">{t.limitItems.map((i) => <li key={i}>{i}</li>)}</ul>

        <H2>{t.codes}</H2>
        <table className="w-full border text-sm"><thead><tr><Th>{t.code_}</Th><Th>{t.meaning}</Th><Th>{t.desc}</Th></tr></thead>
          <tbody>
            {[['200', 'OK'], ['400', 'Bad Request'], ['401', 'Unauthorized'], ['429', 'Too Many Requests'], ['5xx', 'Server Error']].map(([c, m]) => (
              <tr key={c}><Td mono>{c}</Td><Td>{m}</Td><Td>{t.c[c]}</Td></tr>
            ))}
          </tbody>
        </table>

        <H2>{t.best}</H2>
        <ul className="list-disc pl-5 space-y-1">{t.bestItems.map((i) => <li key={i}>{i}</li>)}</ul>

        <p className="mt-10 border-t pt-6">{t.contact} <a href={`mailto:${SUPPORT}`} className="text-rose-600 underline" data-testid="api-docs-support-email">{SUPPORT}</a></p>
      </main>
    </div>
  );
}
